// The public order page a share link opens (/d/<token>): what happened to one
// order, for ANL's customer. No login — the token is the only key — and only
// what the server lets a link show: each delivery attempt's outcome and
// times, what comes next after a failed one (never why), where it went, and
// the stamped pick-up, pallet and BOL photos. In English whatever language
// this browser last used the dispatcher app in.

import { type CSSProperties, useEffect, useState } from "react";

import { ApiError, getPublicOrder, publicPhotoUrl } from "../api/client";
import { clockAt, zoneName } from "../lib/format";
import { LangProvider, type MsgKey, useT } from "../lib/i18n";
import type { PublicDeliveryAttempt, PublicOrder } from "../types";

// The token in a share link's path, or null for any other page.
export function shareToken(pathname: string): string | null {
  return /^\/d\/([A-Za-z0-9_-]+)\/?$/.exec(pathname)?.[1] ?? null;
}

export function OrderPage({ token }: { token: string }) {
  return (
    <LangProvider initial="en">
      <OrderHistory token={token} />
    </LangProvider>
  );
}

function OrderHistory({ token }: { token: string }) {
  const t = useT();
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [problem, setProblem] = useState<MsgKey | null>(null);

  useEffect(() => {
    // A customer's delivery stays out of search engines (the API says so too).
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  useEffect(() => {
    getPublicOrder(token)
      .then(setOrder)
      .catch((e) =>
        setProblem(e instanceof ApiError && e.status === 404 ? "order.invalid" : "order.failed"),
      );
  }, [token]);

  return (
    <main style={page}>
      <p style={brand}>ANL</p>
      {problem && <p>{t(problem)}</p>}
      {!order && !problem && <p style={muted}>{t("app.loading")}</p>}
      {order && (
        <>
          <h1 style={{ margin: "0 0 16px", fontSize: 24 }}>
            {t("order.title", { order: order.order_no })}
          </h1>
          {order.attempts.map((a) => (
            <Attempt key={`${a.plan_date}/${a.picked_up_at}`} attempt={a} token={token} />
          ))}
          <p style={muted}>{t("order.footer")}</p>
        </>
      )}
    </main>
  );
}

const PHOTO_LABEL: Record<PublicDeliveryAttempt["photos"][number]["kind"], MsgKey> = {
  pickup: "order.photo.pickup",
  pallets: "order.photo.pallets",
  signed_bol: "order.photo.signed_bol",
};

function Attempt({ attempt: a, token }: { attempt: PublicDeliveryAttempt; token: string }) {
  const t = useT();
  const at = (iso: string | null) => clockAt(iso, a.timezone);
  const day = new Date(`${a.plan_date}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  const events: [string, string | null][] = [
    [t("order.ev.pickedUp", { depot: a.depot }), a.picked_up_at],
    [t("order.ev.arrived"), a.arrived_at],
    [t("order.ev.left"), a.departed_at],
  ];
  const destination = a.dest_address ?? (a.dest_zip ? `ZIP ${a.dest_zip}` : null);

  return (
    <article style={card}>
      <p style={muted}>
        {day} · {t("order.timesIn", { zone: zoneName(`${a.plan_date}T12:00:00Z`, a.timezone) })}
      </p>
      <h2 style={{ margin: "4px 0 12px", fontSize: 20 }}>{t(`order.status.${a.outcome}`)}</h2>
      {a.customer_status && <p style={{ margin: "0 0 12px" }}>{t(`order.next.${a.customer_status}`)}</p>}
      <ul style={timeline}>
        {events
          .filter(([, when]) => when)
          .map(([label, when]) => (
            <li key={label} style={{ display: "flex", gap: 12 }}>
              <strong style={{ flex: "0 0 72px" }}>{at(when)}</strong>
              <span>{label}</span>
            </li>
          ))}
      </ul>
      {destination && (
        <p style={{ margin: "12px 0" }}>
          {a.pallets !== null
            ? `${destination} · ${t("order.pallets", { n: a.pallets })}`
            : destination}
        </p>
      )}
      {(
        [
          [t("order.pickedUp"), a.photos.filter((p) => p.kind === "pickup")],
          [t("order.proof"), a.photos.filter((p) => p.kind !== "pickup")],
        ] as const
      ).map(
        ([title, photos]) =>
          photos.length > 0 && (
            <section key={title} aria-label={title}>
              <h3 style={{ fontSize: 15, margin: "16px 0 8px" }}>{title}</h3>
              <div style={photoRow}>
                {photos.map((p) => {
                  const src = publicPhotoUrl(token, p.media_id);
                  const label = t(PHOTO_LABEL[p.kind]);
                  return (
                    <figure key={p.media_id} style={{ margin: 0, width: 160 }}>
                      <a href={src} target="_blank" rel="noreferrer">
                        {p.content_type === "application/pdf" ? (
                          // A BOL the dispatcher scanned: opened, not shown inline.
                          <span style={{ ...photo, display: "grid", placeItems: "center" }}>
                            {t("order.openPdf")}
                          </span>
                        ) : (
                          <img src={src} alt={label} style={photo} />
                        )}
                      </a>
                      <figcaption style={muted}>
                        {label} ·{" "}
                        {/* The API is another origin, where `download` is ignored: it is asked. */}
                        <a href={`${src}?download=1`}>{t("order.download")}</a>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </section>
          ),
      )}
    </article>
  );
}

const page: CSSProperties = { maxWidth: 640, margin: "0 auto", padding: "24px 16px 48px" };
const brand: CSSProperties = { fontWeight: 800, letterSpacing: 2, fontSize: 18, margin: "0 0 8px" };
const muted: CSSProperties = { opacity: 0.65, fontSize: 13, margin: 0 };
const card: CSSProperties = {
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};
const timeline: CSSProperties = { listStyle: "none", margin: 0, padding: 0, lineHeight: 1.9 };
const photoRow: CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
// The whole photo: the watermark is along its bottom edge.
const photo: CSSProperties = {
  width: 160,
  height: 213,
  objectFit: "contain",
  background: "rgba(0,0,0,0.04)",
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.12)",
};
